import useI18nExtended from '~/composables/usei18nExtended'
import {useUserStore} from '@/store/user'

/**
 * Form data structure for user prefilling (basic fields)
 */
export interface PrefillData {
  firstName: string
  lastName: string
  email: string
  company: string
  phone: string
  language: string
}

/**
 * Extended form data structure including additional fields
 */
export interface ExtendedPrefillData extends PrefillData {
  country: string
  industryType: string
  specificIndustry: string
  leadMarket: string
}

/**
 * Provides standardized user data prefilling for lead capture forms
 * Pulls data from userStore and i18n for consistent user experience
 * 
 * @returns Object with prefilled form data
 * 
 * @example
 * const { getPrefillData, getExtendedPrefillData } = useFormPrefill()
 * const formData = ref({
 *   ...getPrefillData(),
 *   // ... form-specific fields
 * })
 */
export const useFormPrefill = () => {
  const userStore = useUserStore()
  const i18n = useI18nExtended()

  /**
   * Get prefilled form data from userStore
   * Splits full name into firstName and lastName
   * 
   * @returns Prefilled form data object
   */
  const getPrefillData = (): PrefillData => {
    // Split name into first and last
    const nameParts = userStore.name?.split(' ') || []
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || ''

    return {
      firstName,
      lastName,
      email: userStore.email || '',
      company: (userStore.selectedCustomer as any)?.name || '',
      phone: (userStore.userData as any)?.phone || '',
      language: i18n.locale.value
    }
  }

  /**
   * Get extended prefilled form data
   * Only uses data from userStore, no customer settings prefilling
   * Industry fields are left empty for user to fill in
   * 
   * @returns Extended prefilled form data object
   */
  const getExtendedPrefillData = (): ExtendedPrefillData => {
    const basicData = getPrefillData()

    return {
      ...basicData,
      country: '',
      industryType: '',
      specificIndustry: '',
      leadMarket: ''
    }
  }

  return {
    getPrefillData,
    getExtendedPrefillData
  }
}
